import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { LineItemDto, UpdateLineItemInput } from '@cutlyft/zod-schemas';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';

interface UpdateLineItemApiResponse {
  success: boolean;
  data: LineItemDto;
}

interface UpdateLineItemMutationInput {
  id: number;
  input: UpdateLineItemInput;
}

/**
 * Mutation hook for updating an existing line item.
 * Calls PATCH /line-items/:id.
 * The API re-runs the calculator on any dimension or type change.
 * Invalidates both the list and the individual item cache on success.
 */
export function useUpdateLineItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, input }: UpdateLineItemMutationInput) => {
      const response = await apiClient.patch<UpdateLineItemApiResponse>(`/line-items/${id}`, input);
      return response.data.data;
    },
    onSuccess: (data) => {
      // 3-element prefix so all list queries for this project (any filter/cursor) refetch.
      queryClient.invalidateQueries({ queryKey: ['lineItems', 'list', data.projectId] });
      queryClient.invalidateQueries({ queryKey: queryKeys.lineItems.byId(data.id) });
    },
  });
}
